import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Check, CreditCard } from 'lucide-react'
import { AdaptiveSheet } from '@shared/ui/AdaptiveSheet'
import { formatPrice } from '@features/subscription'
import type { PaymentMethodResponse } from '../../api/types'
import styles from './CoursePaymentModal.module.css'

const NEW_CARD = 'new' as const

export interface CoursePaymentSubmit {
  saveCard: boolean
  /** Сохранённая карта — оплата по ней, без редиректа на ввод карты. */
  paymentMethodId?: number
}

interface CoursePaymentModalProps {
  /** Название курса для заголовка. */
  courseName: string
  /** Цена курса (число). */
  price: number
  /** Привязанные карты пользователя. */
  paymentMethods?: PaymentMethodResponse[]
  isSubmitting?: boolean
  errorText?: string | null
  onSubmit: (payload: CoursePaymentSubmit) => void
  onClose: () => void
}

const getMaskedTail = (masked: string): string => {
  const digits = masked.replace(/\D/g, '')
  const tail = digits.slice(-4)
  return tail ? `**** ${tail}` : masked
}

/**
 * Модалка оплаты курса. Провайдер всегда — Робокасса.
 * Если есть robokassa-карты: выбор сохранённой / новой картой.
 * Если карт нет: один экран с чекбоксом «Сохранить карту».
 */
export const CoursePaymentModal = ({
  courseName,
  price,
  paymentMethods = [],
  isSubmitting = false,
  errorText,
  onSubmit,
  onClose,
}: CoursePaymentModalProps) => {
  const [saveCard, setSaveCard] = useState(true)

  const robokassaCards = useMemo(
    () => paymentMethods.filter((m) => m.provider === 'robokassa'),
    [paymentMethods]
  )
  const hasCards = robokassaCards.length > 0

  const defaultCardId =
    robokassaCards.find((m) => m.is_main)?.payment_method_id ??
    robokassaCards[0]?.payment_method_id ??
    null
  const [selectedCardId, setSelectedCardId] = useState<
    number | typeof NEW_CARD
  >(defaultCardId ?? NEW_CARD)

  useEffect(() => {
    setSelectedCardId(defaultCardId ?? NEW_CARD)
  }, [defaultCardId])

  // Курс всегда оплачивается Робокассой (рубли) — валюту в API не передают.
  const priceLabel = formatPrice(price, 'RUB')

  const submit = () => {
    const payingWithSavedCard = hasCards && selectedCardId !== NEW_CARD
    onSubmit({
      saveCard: payingWithSavedCard ? false : saveCard,
      paymentMethodId: payingWithSavedCard ? selectedCardId : undefined,
    })
  }

  const primaryLabel = isSubmitting
    ? 'Открываем оплату…'
    : `Оплатить ${priceLabel}`

  return (
    <AdaptiveSheet onClose={onClose} height="auto">
      <div className={styles.content}>
        <h3 className={styles.title}>Оплата</h3>

        <div className={styles.summary}>
          <span className={styles.summaryName}>{courseName}</span>
          <span className={styles.summaryPrice}>{priceLabel}</span>
        </div>

        {hasCards && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Способ оплаты</p>
            <div className={styles.optionList}>
              {robokassaCards.map((card) => {
                const isActive = selectedCardId === card.payment_method_id
                return (
                  <button
                    key={card.payment_method_id}
                    type="button"
                    className={`${styles.providerRow} ${isActive ? styles.providerRowActive : ''}`}
                    onClick={() => setSelectedCardId(card.payment_method_id)}
                    disabled={isSubmitting}
                  >
                    <span
                      className={`${styles.checkCircle} ${isActive ? styles.checkCircleActive : ''}`}
                      aria-hidden
                    >
                      {isActive && <Check size={14} strokeWidth={3} />}
                    </span>
                    <span className={styles.providerIcon} aria-hidden>
                      <CreditCard size={18} strokeWidth={2.2} />
                    </span>
                    <span className={styles.providerInfo}>
                      <span className={styles.providerName}>
                        {getMaskedTail(card.masked_card_number)}
                      </span>
                      <span className={styles.providerHint}>
                        {card.card_brand ?? 'Привязанная карта'}
                      </span>
                    </span>
                  </button>
                )
              })}

              <button
                type="button"
                className={`${styles.providerRow} ${selectedCardId === NEW_CARD ? styles.providerRowActive : ''}`}
                onClick={() => setSelectedCardId(NEW_CARD)}
                disabled={isSubmitting}
              >
                <span
                  className={`${styles.checkCircle} ${selectedCardId === NEW_CARD ? styles.checkCircleActive : ''}`}
                  aria-hidden
                >
                  {selectedCardId === NEW_CARD && (
                    <Check size={14} strokeWidth={3} />
                  )}
                </span>
                <span className={styles.providerIcon} aria-hidden>
                  <CreditCard size={18} strokeWidth={2.2} />
                </span>
                <span className={styles.providerInfo}>
                  <span className={styles.providerName}>Новой картой</span>
                  <span className={styles.providerHint}>
                    Оплата с вводом данных карты
                  </span>
                </span>
              </button>
            </div>
          </div>
        )}

        {(!hasCards || selectedCardId === NEW_CARD) && (
          <label className={styles.saveCardRow}>
            <input
              type="checkbox"
              className={styles.visuallyHidden}
              checked={saveCard}
              onChange={(e) => setSaveCard(e.target.checked)}
              disabled={isSubmitting}
            />
            <span
              className={`${styles.saveCardCheckbox} ${saveCard ? styles.saveCardCheckboxActive : ''}`}
              aria-hidden
            >
              {saveCard && <Check size={14} strokeWidth={3} />}
            </span>
            <span className={styles.saveCardLabel}>Сохранить карту</span>
          </label>
        )}

        {errorText && <p className={styles.error}>{errorText}</p>}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.payButton}
            onClick={submit}
            disabled={isSubmitting}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className={styles.backLink}
            onClick={onClose}
            disabled={isSubmitting}
          >
            <ArrowLeft size={14} strokeWidth={2.2} />
            Назад
          </button>
        </div>
      </div>
    </AdaptiveSheet>
  )
}
